import { forwardRef, type ReactNode } from "react";
import { CloseOutlined, EditOutlined, LoadingOutlined, UndoOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Reservation } from "../../../../api/reservations";
import PhoneLink from "../../../../components/PhoneLink";
import { ClockIcon, PersonIcon } from "../../../../components/restaurant-schema/schemaIcons";
import { formatClock } from "../../logic/dayTime";
import { arrivalStatus, reservationEndMs, reservationStartMs } from "../../logic/tableStatus";
import { useReservationFormat } from "../../hooks/useReservationFormat";

type StatusTone = "late" | "soon" | "seated" | "expired" | "muted";

// Редът под името: цвят на текста и на точката пред него.
const STATUS_TEXT: Record<StatusTone, string> = {
  late: "text-danger-ink",
  soon: "text-brand-deep-ink",
  seated: "text-schema-occupied-ink",
  // „Неизпълнена" — приглушен кехлибар: различна от приключила/отказана.
  expired: "text-reservation-expired-ink",
  muted: "text-muted",
};

const STATUS_DOT: Record<StatusTone, string> = {
  late: "bg-danger",
  soon: "bg-brand",
  seated: "bg-schema-occupied-edge",
  expired: "bg-reservation-expired-ink",
  muted: "bg-faint",
};

// Лентата по левия ръб — само за това, което иска внимание сега.
const STATUS_ACCENT: Partial<Record<StatusTone, string>> = {
  late: "bg-danger",
  soon: "bg-brand",
  seated: "bg-schema-occupied-edge",
};

interface CardStatus {
  tone: StatusTone;
  text: string;
  // Закъснението пулсира — единственото, което хостесата трябва да види веднага.
  pulse: boolean;
}

/** Статусът под името: закъснение/скоро, настанени до, приключила, отказана, неизпълнена. */
const useCardStatus = (reservation: Reservation, nowMs: number): CardStatus | null => {
  const { t } = useTranslation();
  const format = useReservationFormat();

  if (reservation.status === "upcoming") {
    const arrival = arrivalStatus(reservation, nowMs);
    if (!arrival) return null;
    const duration = format.duration(arrival.minutes);
    return arrival.kind === "late"
      ? { tone: "late", pulse: true, text: t("reservations.list.late", { duration }) }
      : { tone: "soon", pulse: false, text: t("reservations.list.soon", { duration }) };
  }
  if (reservation.status === "seated" && reservation.seatedAt) {
    return {
      tone: "seated",
      pulse: false,
      text: t("reservations.list.seatedUntil", {
        time: formatClock(Date.parse(reservation.seatedAt)),
        until: formatClock(reservationEndMs(reservation)),
      }),
    };
  }
  if (reservation.status === "completed") return { tone: "muted", pulse: false, text: t("reservations.list.completed") };
  if (reservation.status === "cancelled") return { tone: "muted", pulse: false, text: t("reservations.list.cancelled") };
  if (reservation.status === "expired") return { tone: "expired", pulse: false, text: t("reservations.list.expired") };
  return null;
};

const StatusLine = ({ status }: { status: CardStatus }) => (
  <span
    className={`flex min-w-0 items-center gap-1.5 text-[11.5px] font-bold leading-[1.35] tabular-nums ${STATUS_TEXT[status.tone]}`}
  >
    <span className="relative flex size-1.5 shrink-0">
      {status.pulse && (
        <span className={`absolute inset-0 rounded-full opacity-60 motion-safe:animate-ping ${STATUS_DOT[status.tone]}`} />
      )}
      <span className={`relative size-1.5 rounded-full ${STATUS_DOT[status.tone]}`} />
    </span>
    <span className="truncate">{status.text}</span>
  </span>
);

/**
 * Бутон на картата: „Настани" / „Тръгнаха си" / „Върни", резервното „Откажи"
 * или иконите „Редактирай" / „Отмени настаняването".
 */
export interface CardAction {
  kind: "seat" | "release" | "cancel" | "restore" | "edit" | "unseat";
  pending: boolean;
  disabled: boolean;
  // Защо е изключен или какво ще стане (title) — напр. „Маса 4 е заета".
  hint?: string;
  onClick: () => void;
}

const ACTION_TONE: Record<CardAction["kind"], string> = {
  seat: "border-brand bg-brand-tint text-brand-deep-ink hover:bg-brand-tint2",
  release: "border-shade-3 bg-surface text-ink hover:bg-fill",
  cancel: "border-shade-3 bg-surface text-danger-ink hover:bg-fill",
  restore: "border-brand bg-brand-tint text-brand-deep-ink hover:bg-brand-tint2",
  // Иконните (вторичният вариант на „Настани"): неутрален контур върху
  // прозрачен фон, hover в кехлибарено — не бял квадрат до кехлибарения бутон.
  edit: "border-shade-3 bg-transparent text-body hover:border-brand hover:bg-brand-tint hover:text-brand-deep-ink",
  unseat: "border-shade-3 bg-transparent text-body hover:border-brand hover:bg-brand-tint hover:text-brand-deep-ink",
};

// Второстепенните действия са само икона (името е в title/aria-label) — до
// „Настани"/„Тръгнаха си" иначе не се събират в заглавния ред.
const ACTION_ICON: Partial<Record<CardAction["kind"], ReactNode>> = {
  edit: <EditOutlined />,
  unseat: <UndoOutlined />,
};

// При три действия („Настани" + „Откажи" + редакция) и „Откажи" става икона —
// иначе статусът („Закъснява 2 ч 24 мин") се реже, а на тъч title не се вижда.
const CROWDED_ICON: Partial<Record<CardAction["kind"], ReactNode>> = {
  cancel: <CloseOutlined />,
};

const actionIcon = (kind: CardAction["kind"], crowded: boolean): ReactNode =>
  ACTION_ICON[kind] ?? (crowded ? CROWDED_ICON[kind] : undefined);

/**
 * Карта в списъка — два реда: „19:00 Николай Георгиев" със статуса под името
 * („Закъснява 2 ч 24 мин" / „След 6 мин" / „Настанени 19:10 – 21:10" / „Приключила")
 * + „Настани"/„Тръгнаха си" вдясно; под тях един ред с масата (и региона, ако е
 * друг), гости, продължителност и телефон — масата се съкращава с „…", ако не
 * се събира. Лентата по левия ръб дава тона отдалеч (закъснява/скоро/настанени).
 * Бележката (ако има) е отдолу. Кликът фокусира масата; за
 * клавиатурата и екранните четци входът е заглавието (истински бутон, не
 * обвиващ role=button с вложени бутони).
 */
const ReservationCard = forwardRef<
  HTMLDivElement,
  {
    reservation: Reservation;
    tableLabel: string;
    nowMs: number;
    selected: boolean;
    // Бутоните по статуса; празно — без бутони.
    actions: CardAction[];
    onSelect: () => void;
  }
>(({ reservation, tableLabel, nowMs, selected, actions, onSelect }, ref) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const status = useCardStatus(reservation, nowMs);
  const accent = status ? STATUS_ACCENT[status.tone] : undefined;
  const late = status?.tone === "late";
  const guests = format.guests(reservation.guests);
  const hours = format.hours(reservation.durationMinutes);

  return (
    <div
      ref={ref}
      // Кликът навсякъде по картата е удобство за мишка/тъч; клавиатурата
      // минава през бутона-заглавие.
      onClick={onSelect}
      className={`relative flex cursor-pointer flex-col gap-2 overflow-hidden rounded-[12px] border py-2.5 pl-4 pr-3 transition-colors duration-150 has-[.card-title:focus-visible]:border-brand ${
        selected ? "border-brand bg-brand-tint/60 shadow-[0_0_0_3px_var(--color-schema-halo)]" : "border-shade-2 bg-surface hover:border-shade-3"
      }`}
    >
      {accent && <span aria-hidden className={`absolute inset-y-0 left-0 w-[3px] ${accent}`} />}

      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-pressed={selected}
          onClick={(event) => {
            event.stopPropagation();
            onSelect();
          }}
          title={status?.text}
          className="card-title flex min-w-0 flex-1 cursor-pointer flex-col gap-px border-none bg-transparent p-0 text-left outline-none"
        >
          <span className="flex min-w-0 max-w-full items-baseline gap-2">
            <span
              className={`shrink-0 text-[17px] font-bold leading-[1.3] tabular-nums tracking-[-0.3px] ${late ? "text-danger-ink" : "text-ink"}`}
            >
              {formatClock(reservationStartMs(reservation))}
            </span>
            <span className="min-w-0 truncate text-[15px] font-bold leading-[1.3] tracking-[-0.2px] text-ink">
              {reservation.guestName}
            </span>
          </span>
          {status && <StatusLine status={status} />}
        </button>
        {actions.length > 0 && (
          <div className="flex shrink-0 justify-end gap-1.5">
            {actions.map((action) => {
              const label = t(`reservations.list.${action.kind}`);
              const icon = actionIcon(action.kind, actions.length > 2);
              return (
                <button
                  key={action.kind}
                  type="button"
                  disabled={action.disabled}
                  title={action.hint ?? (icon ? label : undefined)}
                  aria-label={icon ? label : undefined}
                  onClick={(event) => {
                    event.stopPropagation();
                    action.onClick();
                  }}
                  className={`box-border flex h-8 shrink-0 cursor-pointer items-center justify-center gap-1.5 rounded-[8px] border leading-none transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-60 ${
                    icon ? "w-8 px-0 text-[13px]" : "px-3 text-[12.5px] font-bold"
                  } ${ACTION_TONE[action.kind]}`}
                >
                  {action.pending ? <LoadingOutlined /> : icon}
                  {!icon && label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Един ред: масата отстъпва първа („Маса 11 · Тесто…"), телефонът остава цял. */}
      <div
        className="flex min-w-0 items-center gap-2.5 whitespace-nowrap text-[12.5px] font-medium text-body"
        title={[tableLabel, guests, hours, reservation.phone].filter(Boolean).join(" · ")}
      >
        <span className="min-w-0 truncate font-bold text-ink">{tableLabel}</span>
        <span className="flex shrink-0 items-center gap-1">
          <PersonIcon className="size-3 text-muted" />
          {guests}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <ClockIcon className="size-3 text-muted" />
          {hours}
        </span>
        {/* Набира при клик; кликът не избира картата. */}
        {reservation.phone && <PhoneLink phone={reservation.phone} className="ml-auto shrink-0 text-ink-2" />}
      </div>

      {reservation.note && (
        <div className="rounded-[8px] bg-fill px-2.5 py-1.5 text-[12.5px] leading-[1.45] text-ink-2">
          {reservation.note}
        </div>
      )}
    </div>
  );
});

ReservationCard.displayName = "ReservationCard";

export default ReservationCard;
